import React from 'react';
import {
  refreshWorkSummary,
  setWorkItemsContext,
  useWorkItemsStore,
} from '@/stores/workItemsStore';
import { usePermissionStore } from '@/stores/permissionStore';
import { useModel } from '@umijs/max';
import { PAYMENT_VOUCHER_PERMISSION } from '@/services/paymentVoucherTodo';
import {
  refreshPaymentVoucherTodo,
  setPaymentVoucherTodoContext,
} from '@/stores/paymentVoucherTodoStore';
import { usePurchasePaymentUpdates } from '@/hooks/usePurchasePaymentUpdates';
import { startPageDataRefresh } from '@/utils/pageDataRefresh';

type Props = { userId?: string | number; permissionCodes?: string[] };

export const WorkItemsController: React.FC<Props> = ({ userId, permissionCodes = [] }) => {
  const voucherEligible = usePermissionStore((state) => {
    const scope = state.userPermissionMap.get(PAYMENT_VOUCHER_PERMISSION);
    return (
      state.isInitialized &&
      state.userId === String(userId) &&
      (scope === 'self' || scope === 'team')
    );
  });
  usePurchasePaymentUpdates(refreshPaymentVoucherTodo, voucherEligible);
  const { setInitialState } = useModel('@@initialState');
  const available = useWorkItemsStore((state) =>
    Boolean(state.context && state.summary?.enabled && state.summary.pages.length),
  );
  // Bridge availability to ProLayout so it omits the whole action slot,
  // including its padding, until this account has a configured page.
  React.useEffect(() => {
    const availableFor = available && userId ? String(userId) : undefined;
    void setInitialState((state) => {
      if (!state || String(state.currentUser?.id) !== String(userId)) return state;
      if (state.workItemsAvailableFor === availableFor) return state;
      return { ...state, workItemsAvailableFor: availableFor };
    });
  }, [available, userId, setInitialState]);
  const permissions = [...permissionCodes].sort().join(',');
  React.useLayoutEffect(() => {
    const syncContext = () => {
      const token = localStorage.getItem('token');
      const permissionState = usePermissionStore.getState();
      const livePermissionSignature =
        permissionState.isInitialized && permissionState.userId === String(userId)
          ? JSON.stringify({
              scopes: [...permissionState.userPermissionMap.entries()].sort(([a], [b]) =>
                a.localeCompare(b),
              ),
              teams: [...permissionState.teamIds].sort(),
              roles: permissionState.roleAssignments.map((role) => String(role.roleId)).sort(),
              rolesVersion: permissionState.rolesVersion,
            })
          : permissions;
      const context = userId && token ? `${userId}:${token}:${livePermissionSignature}` : null;
      setWorkItemsContext(context);
      const voucherScope = permissionState.userPermissionMap.get(PAYMENT_VOUCHER_PERMISSION);
      setPaymentVoucherTodoContext(
        context &&
          permissionState.isInitialized &&
          permissionState.userId === String(userId) &&
          (voucherScope === 'team' || voucherScope === 'self')
          ? context
          : null,
      );
      return context;
    };
    const refresh = () => {
      const context = syncContext();
      if (context && document.visibilityState === 'visible') {
        void refreshWorkSummary();
        void refreshPaymentVoucherTodo();
      }
    };
    refresh();
    const timer = startPageDataRefresh(refresh);
    document.addEventListener('visibilitychange', refresh);
    const unsubscribe = usePermissionStore.subscribe(() => {
      const before = useWorkItemsStore.getState().context;
      const after = syncContext();
      if (after && after !== before && document.visibilityState === 'visible') {
        void refreshWorkSummary();
        void refreshPaymentVoucherTodo();
      }
    });
    return () => {
      if (timer !== undefined) window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      unsubscribe();
      setWorkItemsContext(null);
      setPaymentVoucherTodoContext(null);
    };
  }, [userId, permissions]);
  return null;
};
